import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ShieldCheck, Landmark, Copy, Check, CreditCard, Loader2 } from 'lucide-react';
import { company } from '../mock';
import { createOrder, verifyPayment, loadRazorpay } from '../lib/api';
import { useToast } from '../hooks/use-toast';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FloatingWhatsApp from '../components/FloatingWhatsApp';

const sections = [
  {
    n: 1,
    title: 'Travel Terms & Conditions',
    sub: 'Authorized Partnership & Service Provider Clause',
    paras: [
      'Travel Era Holiday operates as a contracted partner with authorized service providers for hotels, flights, visa assistance, sightseeing, transfers, and other travel-related services. All bookings and arrangements are facilitated through our authorized partner network and are subject to their individual operating policies, availability, and service terms.',
      'We do not own or operate airlines, hotels, transport companies, visa facilitation centers, or other third-party service providers. Therefore, Travel Era Holiday shall not be liable for any delays, service changes, cancellations, or operational issues caused by third parties beyond our control.',
      'By booking a holiday package, the traveller acknowledges and accepts that all services are delivered through authorized contracted partners and agrees to comply with the respective terms of each service provider.',
    ],
  },
  {
    n: 2,
    title: 'Booking Policies',
    points: [
      'A booking is confirmed only upon receipt of the required advance payment.',
      'Final package cost may change depending on availability, fare fluctuations, currency exchange rates, or any increase in taxes/fees imposed by suppliers or government authorities.',
      'All traveller details, including passport copies, must be provided accurately at the time of booking.',
      'Any special requests (e.g., room preference, meal preference, early check-in, etc.) are subject to availability and cannot be guaranteed.',
    ],
  },
  {
    n: 3,
    title: 'Cancellations & Refunds',
    points: [
      'Cancellation charges apply as per the policy of airlines, hotels, visa offices, and other third-party service providers.',
      'Refunds, if applicable, will be processed only after receiving refunds from respective suppliers.',
      'Visa fees, insurance fees, convenience fees, and service charges are non-refundable under any circumstances.',
      'No refund for no-show, unused services, or early return from the trip.',
    ],
  },
  {
    n: 4,
    title: 'Visa & Documentation',
    points: [
      'Travel Era Holiday provides visa assistance through authorized partners but does not guarantee visa approval.',
      'Visa approval or rejection is at the sole discretion of the Embassy/Consulate.',
      'In case of visa rejection, all third-party charges remain non-refundable.',
      'Travellers must ensure their passport has a minimum validity of 6 months from the travel date.',
    ],
  },
  {
    n: 5,
    title: 'Travel Insurance Policy',
    points: [
      'Travel insurance is strongly recommended for all international trips.',
      'Insurance claims must be made directly with the insurance provider.',
      'Travel Era Holiday is not responsible for any claim rejection by the insurance company.',
    ],
  },
  {
    n: 6,
    title: 'Itinerary & Service Changes',
    points: [
      'Itinerary may change depending on weather conditions, operational issues, or local events.',
      'Hotels, flights, and sightseeing tours are subject to availability at the time of booking.',
      'In case of unavailability, an equivalent alternative of similar category will be provided.',
    ],
  },
  {
    n: 7,
    title: "Traveller's Responsibility",
    points: [
      'Travellers must carry valid travel documents such as passport, visa, travel insurance, and IDs.',
      'Any loss of travel documents, baggage, or personal belongings is the traveller\u2019s responsibility.',
      'Travellers must follow local laws, regulations, and customs during their trip.',
      'Any damage caused to hotel property, vehicles, or equipment must be compensated directly by the traveller.',
    ],
  },
  {
    n: 8,
    title: 'Health, Safety & Liability',
    points: [
      'Travellers must inform us of any medical conditions prior to booking.',
      'Travel Era Holiday is not liable for any injury, illness, accident, or death during travel.',
      'All travel is undertaken at the traveller\u2019s own risk.',
    ],
  },
  {
    n: 9,
    title: 'Force Majeure',
    paras: [
      'Travel Era Holiday shall not be held responsible for any delays, cancellations, or interruptions caused by events beyond control, including natural disasters, political unrest, pandemic/epidemic, strikes or government restrictions, and technical failures by airlines or hotels. No refunds or compensation will be provided for force majeure situations.',
    ],
  },
  {
    n: 10,
    title: 'Acceptance of Terms',
    paras: [
      'By making any booking with Travel Era Holiday, the traveller confirms that they have read, understood, and agreed to all the above Terms & Conditions.',
    ],
  },
];

function AccountDetails() {
  const [copied, setCopied] = React.useState('');
  const b = company.bank;
  const rows = [
    ['Bank Name', b.bankName],
    ['Account Name', b.accountName],
    ['Account Number', b.accountNumber],
    ['IFSC Code', b.ifsc],
    ['UPI ID', b.upi],
    ['Branch', b.branch],
  ];
  const copy = (val) => {
    navigator.clipboard?.writeText(val);
    setCopied(val);
    setTimeout(() => setCopied(''), 2000);
  };
  const qr = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(`upi://pay?pa=${b.upi}&pn=Travel Era Holiday`)}`;

  return (
    <div id="account" className="scroll-mt-28 mt-16">
      <div className="flex items-center gap-3 mb-6">
        <span className="grid place-items-center h-11 w-11 rounded-xl bg-[#0B2545] text-[#ff7a45]"><Landmark size={22} /></span>
        <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-[#0B2545]">Company Account Details</h2>
      </div>
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-2xl border border-slate-100 overflow-hidden shadow-[0_10px_30px_rgba(11,37,69,0.06)]">
          {rows.map(([k, v], i) => (
            <div key={k} className={`flex items-center ${i % 2 ? 'bg-white' : 'bg-[#f6f8fb]'}`}>
              <div className="w-40 sm:w-52 shrink-0 px-5 py-4 font-semibold text-[#0B2545] border-r border-slate-100">{k}</div>
              <div className="flex-1 px-5 py-4 text-slate-700 flex items-center justify-between gap-3">
                <span className="break-all">{v}</span>
                <button onClick={() => copy(v)} className="text-slate-400 hover:text-[#EA5019] transition shrink-0" aria-label={`Copy ${k}`}>
                  {copied === v ? <Check size={16} className="text-[#22a06b]" /> : <Copy size={16} />}
                </button>
              </div>
            </div>
          ))}
        </div>
        <div className="rounded-2xl bg-[#0B2545] p-6 text-center text-white flex flex-col items-center justify-center">
          <p className="font-heading font-bold text-lg mb-4">Scan to Pay</p>
          <div className="bg-white rounded-xl p-3">
            <img src={qr} alt="UPI QR code" className="h-40 w-40" />
          </div>
          <p className="text-white/70 text-sm mt-4">UPI: {b.upi}</p>
        </div>
      </div>

      <AdvancePayment />
    </div>
  );
}

function AdvancePayment() {
  const { toast } = useToast();
  const [amount, setAmount] = React.useState('');
  const [paying, setPaying] = React.useState(false);
  const FEE = 0.02;
  const base = Number(amount) || 0;
  const fee = Math.round(base * FEE);
  const total = base + fee;
  const fmt = (n) => '\u20B9' + n.toLocaleString('en-IN');

  const pay = async () => {
    if (base < 1) { toast({ title: 'Enter an amount', description: 'Please enter the advance amount you wish to pay.' }); return; }
    setPaying(true);
    try {
      const ok = await loadRazorpay();
      if (!ok) { toast({ title: 'Unable to load payment gateway' }); setPaying(false); return; }
      const order = await createOrder({ amount: total, package: `Advance payment (incl. 2% fee) \u2014 base ${fmt(base)}` });
      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        order_id: order.order_id,
        name: company.name,
        description: `Advance Payment (incl. 2% gateway fee)`,
        image: company.logo,
        theme: { color: '#EA5019' },
        handler: async (res) => {
          try { await verifyPayment(res); toast({ title: 'Payment successful!', description: `We have received your advance of ${fmt(total)}. Our team will contact you shortly.` }); setAmount(''); }
          catch (e) { toast({ title: 'Payment could not be verified', description: 'If money was deducted, please contact us with your payment ID.' }); }
        },
        modal: { ondismiss: () => setPaying(false) },
      });
      rzp.on('payment.failed', () => { toast({ title: 'Payment failed', description: 'Please try again.' }); setPaying(false); });
      rzp.open();
      setPaying(false);
    } catch (e) { toast({ title: 'Could not start payment' }); setPaying(false); }
  };

  return (
    <div id="pay" className="scroll-mt-28 mt-14 rounded-2xl border border-[#EA5019]/20 bg-[#fff7f3] p-6 md:p-8">
      <div className="flex items-center gap-3 mb-2">
        <span className="grid place-items-center h-11 w-11 rounded-xl bg-[#EA5019] text-white"><CreditCard size={22} /></span>
        <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-[#0B2545]">Pay Advance Online</h2>
      </div>
      <p className="text-slate-600 mb-5 max-w-2xl">Securely pay your booking advance via Razorpay (cards, UPI, netbanking & wallets). A <span className="font-semibold text-[#EA5019]">2% payment gateway fee</span> is added to online payments. To avoid this fee, use the bank transfer / UPI details above.</p>
      <div className="grid sm:grid-cols-3 gap-4 items-end max-w-2xl">
        <div className="sm:col-span-1">
          <label className="text-sm font-medium text-[#0B2545]">Advance amount (₹)</label>
          <input type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="e.g. 25000" className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-3 text-[#0B2545] outline-none focus:border-[#EA5019] focus:ring-2 focus:ring-[#EA5019]/20 bg-white" />
        </div>
        <div className="sm:col-span-2 flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
          <span className="text-slate-500">Amount: <span className="font-semibold text-[#0B2545]">{fmt(base)}</span></span>
          <span className="text-slate-500">+ 2% fee: <span className="font-semibold text-[#0B2545]">{fmt(fee)}</span></span>
          <span className="text-slate-500">Total payable: <span className="font-heading font-extrabold text-[#EA5019] text-base">{fmt(total)}</span></span>
        </div>
      </div>
      <button onClick={pay} disabled={paying} className={`mt-5 inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-[#EA5019] text-white font-semibold hover:bg-[#cf4413] transition shadow-[0_10px_26px_rgba(234,80,25,0.3)] ${paying ? 'opacity-70 cursor-not-allowed' : ''}`}>
        {paying ? <><Loader2 size={19} className="animate-spin" /> Processing…</> : <><CreditCard size={19} /> Pay {total > 0 ? fmt(total) : 'Advance'} Now</>}
      </button>
    </div>
  );
}

export default function Terms() {
  return (
    <div className="bg-white">
      <Header />
      <section className="relative pt-32 pb-14 bg-[#0B2545] overflow-hidden">
        <div className="absolute inset-0 opacity-[0.07] bg-cover bg-center" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=1600&q=60')" }} />
        <div className="relative max-w-5xl mx-auto px-5 md:px-8">
          <Link to="/" className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm mb-4"><ArrowLeft size={16} /> Back to home</Link>
          <p className="section-eyebrow text-[#ff9a6a] mb-2"><span className="h-px w-8 bg-[#EA5019] inline-block" /> BOOKING POLICY</p>
          <h1 className="font-heading text-3xl md:text-5xl font-extrabold text-white flex items-center gap-3"><ShieldCheck className="text-[#ff7a45]" size={38} /> Terms & Conditions</h1>
          <p className="text-white/75 mt-3 max-w-2xl">Please read these Terms &amp; Conditions carefully before booking any trip or service with us. By using our website or making a booking, you agree to these terms.</p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-5 md:px-8 py-14">
        <div className="space-y-10">
          {sections.map((s) => (
            <div key={s.n}>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-[#0B2545] flex items-start gap-3">
                <span className="grid place-items-center h-8 w-8 shrink-0 rounded-lg bg-[#EA5019]/10 text-[#EA5019] text-sm font-extrabold mt-0.5">{s.n}</span>
                <span>{s.title}{s.sub && <span className="block text-sm font-medium text-[#EA5019] mt-1">{s.sub}</span>}</span>
              </h2>
              <div className="mt-3 pl-11 space-y-3">
                {s.paras && s.paras.map((p) => <p key={p} className="text-slate-600 leading-relaxed">{p}</p>)}
                {s.points && (
                  <ul className="space-y-2">
                    {s.points.map((p) => (
                      <li key={p} className="flex items-start gap-2.5 text-slate-600 leading-relaxed">
                        <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#EA5019] shrink-0" /> {p}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          ))}
        </div>

        <AccountDetails />
      </section>
      <Footer />
      <FloatingWhatsApp />
    </div>
  );
}
